import { ReactNode, useEffect } from 'react';
import { migrateLegacyHubToWorkspace } from '@legacy/adapters';
import { DEMO_PROJECT_ID, getOwnerLicenseBody, hydrateWorkspaceFromOwnerPreset, writeOwnerWorkspacePreset } from '@shared/domain';
import { hasExistingWorkspace, saveWorkspace } from '@shared/storage';
import { migrateLegacyDevisIfNeeded } from '@shared/storage/repositories/devisRepository';
import { migrateLegacyInvoicesIfNeeded } from '@shared/storage/repositories/missioInvoiceRepository';
import { LicenseState } from '@shared/license';
import { SyncInit, SyncProvider } from '@shared/sync';
import { useWorkspaceStore } from '@shared/state';
import { Workspace } from '@shared/types';

// AppProviders will host global providers like:
// - ThemeProvider (future)
// - QueryClientProvider (future, for remote data)
// - AuthProvider (future)
// - SyncProvider (future)
//
// For now, this is a placeholder that just renders children
// Providers will be added as needed in future phases

export function AppProviders({ children }: { children: ReactNode }) {
  const theme = useWorkspaceStore((state) => state.preferences.theme);

  useEffect(() => {
    void migrateLegacyDevisIfNeeded();
    void migrateLegacyInvoicesIfNeeded();

    const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const demoMode = search?.get('demo') === '1';

    if (hasExistingWorkspace()) {
      useWorkspaceStore.getState().hydrate();
      useWorkspaceStore.getState().syncFromDexie();
      useWorkspaceStore.getState().seedDemoProject();
      if (demoMode) {
        useWorkspaceStore.getState().setCurrentProjectId(DEMO_PROJECT_ID);
        const demoSession = useWorkspaceStore.getState().calcMissionSessions.find((session) => session.projectId === DEMO_PROJECT_ID);
        if (demoSession) {
          useWorkspaceStore.getState().setCurrentCalcMissionSessionId(demoSession.id);
        }
      }
      return;
    }

    const migrated = migrateLegacyHubToWorkspace();
    const hasMigratedData =
      migrated.projects.length > 0 ||
      migrated.evaluations.length > 0 ||
      migrated.sipocs.length > 0 ||
      migrated.moscows.length > 0 ||
      migrated.racis.length > 0 ||
      migrated.dealRooms.length > 0 ||
      migrated.cras.length > 0 ||
      migrated.treasuries.length > 0;

    if (hasMigratedData) {
      saveWorkspace(migrated);
      useWorkspaceStore.setState({ ...migrated });
      useWorkspaceStore.getState().seedDemoProject();
      if (demoMode) {
        useWorkspaceStore.getState().setCurrentProjectId(DEMO_PROJECT_ID);
        const demoSession = useWorkspaceStore.getState().calcMissionSessions.find((session) => session.projectId === DEMO_PROJECT_ID);
        if (demoSession) {
          useWorkspaceStore.getState().setCurrentCalcMissionSessionId(demoSession.id);
        }
      }
      return;
    }

    useWorkspaceStore.getState().seedDemoProject();
    if (demoMode) {
      useWorkspaceStore.getState().setCurrentProjectId(DEMO_PROJECT_ID);
      const demoSession = useWorkspaceStore.getState().calcMissionSessions.find((session) => session.projectId === DEMO_PROJECT_ID);
      if (demoSession) {
        useWorkspaceStore.getState().setCurrentCalcMissionSessionId(demoSession.id);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const applyOwnerPreset = (license?: Partial<LicenseState> | null) => {
      const fallback = window.IndxLicense?.getState?.() as Partial<LicenseState> | undefined;
      const ownerBody = getOwnerLicenseBody({
        kind: license?.kind ?? fallback?.kind ?? null,
        body: license?.body ?? fallback?.body ?? null,
        isPro: license?.isPro ?? fallback?.isPro ?? false,
      });

      if (!ownerBody) return;

      const state = useWorkspaceStore.getState();
      const { currentProject: _currentProject, ...workspace } = state;
      const hydrated = hydrateWorkspaceFromOwnerPreset(workspace, ownerBody);
      saveWorkspace(hydrated);
      useWorkspaceStore.setState({ ...hydrated });
      writeOwnerWorkspacePreset(ownerBody, hydrated);
    };

    applyOwnerPreset();

    const handleLicenseChange = (event: Event) => {
      const detail = (event as CustomEvent<Partial<LicenseState>>).detail;
      applyOwnerPreset(detail);
    };

    let presetTimer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = useWorkspaceStore.subscribe((state) => {
      clearTimeout(presetTimer);
      presetTimer = setTimeout(() => {
        const ownerBody = getOwnerLicenseBody(window.IndxLicense?.getState?.() as Partial<LicenseState> | undefined);
        if (!ownerBody) return;
        const { currentProject: _currentProject, ...workspace } = state;
        writeOwnerWorkspacePreset(ownerBody, workspace as Workspace);
      }, 500);
    });

    window.addEventListener('indxlicense:changed', handleLicenseChange);

    return () => {
      unsubscribe();
      window.removeEventListener('indxlicense:changed', handleLicenseChange);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  return <SyncProvider><SyncInit />{children}</SyncProvider>;
}
